import { isAxiosError } from "axios";

import { PillVariants } from "src/base-components/Pill";
import { CaseDeserialized } from "src/cases/queries/casesTypes";
import {
  CasePropertiesValue,
  CaseTemplateAdditionalInputTypeEnum,
  CaseTemplateVersionAttributes,
  PredefinedValue,
} from "src/clients/case-management";
import { getRandomDate } from "src/datasets/dataTemplateUtils";
import { toastActions } from "src/design-system/Toast/utils";
import { isPreconditionError } from "src/utils/predicates";
import { assertUnreachable } from "src/utils/typeUtils";

export const createCaseDataTemplate = (
  attributes?: CaseTemplateVersionAttributes,
) => {
  const { case_properties = {}, additional_inputs = {} } = attributes ?? {};
  const template = {} as Record<string, PropertyValue>;

  for (const key in case_properties) {
    const property = case_properties[key];
    template[key] = toPropertyValue(
      property.type,
      "values" in property ? property["values"] : undefined,
    );
  }

  for (const key in additional_inputs) {
    const input = additional_inputs[key];
    template[key] = toPropertyValue(input.type);
  }

  return template;
};

export const toPropertyValue = (
  type: PropertyType,
  values?: PredefinedValue[],
) => {
  switch (type) {
    case "string":
      return "";
    case "array":
      return [];
    case "object":
      return {};
    case "datetime":
      return getRandomDate();
    case "enum":
      return values?.[0]?.value ?? null;
    case "tags": {
      const isTag = (tag: unknown): tag is PredefinedValue =>
        !!tag && !!(tag as PredefinedValue).value;
      return (values ?? [])
        .filter(isTag)
        .slice(0, 2)
        .map((tag) => tag.value);
    }
    case "entity_ref":
      return "entity_id";
    case "event_ref":
      return "event_id";
    default:
      assertUnreachable(type);
      return null;
  }
};

type PropertyType =
  | CasePropertiesValue["type"]
  | CaseTemplateAdditionalInputTypeEnum;

type PropertyValue =
  | string
  | string[]
  | number
  | true
  | Array<never>
  | Record<string, never>
  | null;

export const getPropertyVariant = (
  value: string,
  property?: CasePropertiesValue,
): PillVariants => {
  if (!value || !property) return "gray";

  if (property.type === "enum") {
    const enumValue = property.values?.find((v) => v.value === value);
    if (enumValue?.color) {
      return enumValue.color as PillVariants;
    }
  }
  return "gray";
};

export const getStatusVariant = (
  status: string,
  templateData?: CaseDeserialized["data"]["template_version"],
): PillVariants => {
  return getPropertyVariant(status, templateData?.case_properties?.status);
};

export const getPriorityVariant = (
  priority: string,
  templateData?: CaseDeserialized["data"]["template_version"],
): PillVariants => {
  return getPropertyVariant(priority, templateData?.case_properties?.priority);
};

export const onAssignCasesError = (
  error: unknown,
  toastOptions: {
    id: string;
    action: "claiming" | "assigning";
  },
) => {
  if (isAxiosError(error)) {
    toastActions.failure({
      id: toastOptions.id,
      title: `Error ${toastOptions.action} cases`,
      description: error.response?.data.errors[0]?.detail ?? "Unknown error",
    });
  } else {
    toastActions.failure({
      id: toastOptions.id,
      title: `Error ${toastOptions.action} cases`,
      description: `An error occurred while ${toastOptions.action} the cases. Please try again later.`,
    });
  }
};

export const onArchiveCasesError = (
  error: unknown,
  toastOptions?: { id?: string },
) => {
  if (isPreconditionError(error)) {
    toastActions.failure({
      id: toastOptions?.id,
      title: "Archiving failed",
      description:
        "An error occurred while archiving. Please refresh the page and try again.",
    });
    return;
  }

  if (isAxiosError(error)) {
    toastActions.failure({
      id: toastOptions?.id,
      title: "Archiving failed",
      description: error.response?.data.errors[0]?.detail ?? "Unknown error",
    });
    return;
  }

  toastActions.failure({
    id: toastOptions?.id,
    title: "Archiving failed",
    description:
      "Unexpected error occurred while archiving. Please try again later.",
  });
};

/**
 *
 * Type predicate to check if a property is the status property
 * @param property - property to check
 */
export const isStatusProperty = (
  property?: CasePropertiesValue,
): property is Extract<CasePropertiesValue, { type: "enum"; key: "status" }> =>
  !!property &&
  "key" in property &&
  property.key === "status" &&
  property.type === "enum";
